<template>
  <!-- Worüber die Postfächer informiert werden (Kundenfeedback Autohaus
       Zwerger, 14.10.2026: „Wir werden gerade geflutet"). Postfächer ohne
       Nutzerkonto (Abteilung → Standort → Unternehmen, Sammel-Postfach)
       erhielten in der Sammel-Benachrichtigung bisher immer alle Themen.
       Kundenfeedback Kanzlei Polz 18.10.2026 Teil 2 (V): „Den Punkt ‚Themen
       für Postfächer' verstehe ich bis heute nicht" – deshalb heißt die Karte
       jetzt wie die Frage, die sie beantwortet, hat dieselbe ruhige Optik wie
       die übrigen Karten und steht neben den Postfächern, für die sie gilt.
       Änderungen werden sofort gespeichert – wie im Profil. -->
  <section :id="anchorId ?? undefined" aria-labelledby="mtc-title" class="mtc" data-testid="mailbox-topics-card">
    <header class="mtc__head">
      <span class="mtc__icon"><MailMinus :size="20" /></span>
      <div class="mtc__head-text">
        <h3 id="mtc-title" class="mtc__title">{{ $gt('Worüber die Postfächer informiert werden') }}</h3>
        <p class="mtc__sub">
          {{ $gt('Gilt für alle gemeinsamen Postfächer Ihres Unternehmens (Abteilung, Standort, Unternehmen, Sammel-Postfach): Diese Themen stehen in der Sammel-Benachrichtigung, die KARS alle 30 Minuten dorthin schickt. Abgewählte Themen kommen dort nicht mehr an.') }}
        </p>
      </div>
    </header>

    <v-skeleton-loader v-if="loading" type="list-item, list-item, list-item, list-item" />
    <p v-else-if="loadError" class="mtc__error">{{ loadError }}</p>
    <template v-else>
      <ul class="mtc__list">
        <li v-for="t in topics" :key="t.topic">
          <label class="mtc__row" :class="{ 'is-off': !t.enabled, 'is-locked': !canEdit }">
            <input
              :checked="t.enabled"
              :data-testid="`mailbox-topic-${t.topic}`"
              :disabled="!canEdit || saving"
              type="checkbox"
              @change="toggle(t.topic, ($event.target as HTMLInputElement).checked)"
            >
            <span class="mtc__row-label">{{ $gt(t.label) }}</span>
            <span class="mtc__row-state">{{ t.enabled ? $gt('erhalten') : $gt('abbestellt') }}</span>
          </label>
        </li>
      </ul>
      <p class="mtc__status" data-testid="mailbox-topics-summary">{{ $gt(summary) }}</p>
    </template>

    <ul class="mtc__facts">
      <li>
        <UserRound :size="14" />
        <span v-if="personalInline">
          <strong>{{ $gt('Und Ihre eigenen E-Mails?') }}</strong>
          {{ $gt('Diese Auswahl gilt nur für die Postfächer. Was Sie persönlich erhalten, wählen Sie unter „Für mich“ – jede Person für sich.') }}
        </span>
        <span v-else>
          <strong>{{ $gt('Und Ihre eigenen E-Mails?') }}</strong>
          {{ $gt('Diese Auswahl gilt nur für die Postfächer. Was Sie persönlich erhalten, wählen Sie unter „Persönliche Einstellungen“ – jede Person für sich.') }}
          <RouterLink class="mtc__link" data-testid="mailbox-topics-profile-link" :to="profileRoute">
            {{ $gt('Meine Benachrichtigungen öffnen') }}
          </RouterLink>
        </span>
      </li>
      <li v-if="!loading && !canEdit">
        <Lock :size="14" />
        <span>{{ $gt('Nur Administratoren und Unternehmensleiter können die Auswahl ändern.') }}</span>
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
  import { Lock, MailMinus, UserRound } from 'lucide-vue-next'
  import { computed, onMounted, ref } from 'vue'
  import { gt } from '@/i18n/gt'
  import { type MailboxTopic, summarizeMailboxTopics, toggleMailboxTopic } from '@/pages/pageCommunicationSettings/mailboxTopicsModel'
  import { loadMailboxTopics, saveMailboxTopics } from '@/services/notificationRecipients'
  import { useSnackbarStore } from '@/stores/snackbarStore'

  // Sprungziel ?bereich=postfach-themen auf der Seite; im zentralen Dialog
  // ohne ID, damit sie nicht doppelt vorkommt.
  // personalInline: im Dialog „E-Mail-Empfang & Benachrichtigungen" stehen die
  // persönlichen Themen direkt darüber („Für mich") – dann Verweis dorthin
  // statt Link ins Profil.
  withDefaults(defineProps<{ anchorId?: string | null, personalInline?: boolean }>(), {
    anchorId: 'postfach-themen',
    personalInline: false,
  })

  const profileRoute = { name: 'PageUserSettings', query: { abschnitt: 'benachrichtigungen' } }

  const topics = ref<MailboxTopic[]>([])
  const canEdit = ref(false)
  const loading = ref(false)
  const saving = ref(false)
  const loadError = ref('')

  const summary = computed(() => summarizeMailboxTopics(topics.value))

  async function load () {
    loading.value = true
    loadError.value = ''
    try {
      const settings = await loadMailboxTopics()
      topics.value = settings.topics
      canEdit.value = settings.canEdit
    } catch (error) {
      loadError.value = (error as Error)?.message || gt('Die Themen der Postfächer konnten nicht geladen werden.')
    } finally {
      loading.value = false
    }
  }

  async function toggle (key: string, enabled: boolean) {
    const previous = topics.value
    topics.value = toggleMailboxTopic(previous, key, enabled)
    saving.value = true
    try {
      await saveMailboxTopics([{ topic: key, enabled }])
    } catch (error) {
      topics.value = previous
      useSnackbarStore().showSnackbar({
        message: (error as Error)?.message || gt('Die Auswahl konnte nicht gespeichert werden.'),
        type: 'error',
      })
    } finally {
      saving.value = false
    }
  }

  onMounted(load)
</script>

<style lang="scss" scoped>
.mtc {
  --area-accent: #0076e5;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px 22px;
  border: 1px solid var(--border-grey-color, #d0d5dd);
  border-radius: 16px;
  background: #fff;
  overflow: hidden;
  scroll-margin-top: 80px;

  &::before {
    content: "";
    position: absolute;
    inset: 0 0 auto;
    height: 3px;
    background: var(--area-accent);
  }

  &:target { box-shadow: 0 0 0 3px rgba(0, 118, 229, 0.25); }
}

.mtc__head { display: flex; align-items: flex-start; gap: 14px; }
.mtc__icon {
  display: flex; align-items: center; justify-content: center;
  width: 44px; height: 44px; border-radius: 12px;
  color: #fff; background: var(--area-accent); flex-shrink: 0;
}
.mtc__head-text { flex: 1; min-width: 0; }
.mtc__title { margin: 0; font-size: 0.98rem; font-weight: 750; color: var(--dark-color, #323232); }
.mtc__sub { margin: 4px 0 0; font-size: 0.8rem; line-height: 1.5; color: var(--grey-color, #5f5f5f); }

.mtc__list {
  list-style: none; margin: 0; padding: 0;
  display: flex; flex-direction: column; gap: 6px;
}
.mtc__row {
  display: flex; align-items: center; gap: 10px;
  padding: 9px 12px; border-radius: 10px;
  border: 1px solid #bfdbfe; background: #f5f9ff;
  font-size: 0.86rem; color: var(--dark-color, #323232);
  cursor: pointer; user-select: none;
  transition: background .15s, border-color .15s, color .15s;

  input { accent-color: var(--area-accent); cursor: pointer; margin: 0; }
  &:hover:not(.is-locked) { border-color: #93c5fd; }
  &:focus-within { outline: 2px solid var(--area-accent); outline-offset: 2px; }

  &.is-off {
    border-color: var(--border-grey-color, #d0d5dd);
    background: #f8f9fb;
    color: var(--grey-color, #5f5f5f);
    .mtc__row-label { text-decoration: line-through; text-decoration-color: #9ca3af; }
  }
  &.is-locked { cursor: default; input { cursor: default; } }
}
.mtc__row-label { flex: 1; min-width: 0; font-weight: 600; }
.mtc__row-state {
  flex-shrink: 0; font-size: 0.72rem; font-weight: 600; letter-spacing: .02em;
  color: #0b5cad;
  .is-off & { color: var(--grey-color, #5f5f5f); }
}

.mtc__link {
  font-weight: 600; color: #0b5cad; text-decoration: underline; text-underline-offset: 2px;
  &:hover { color: #1d4ed8; }
  &:focus-visible { outline: 2px solid var(--area-accent); outline-offset: 2px; border-radius: 3px; }
}
.mtc__facts strong { color: var(--dark-color, #323232); font-weight: 650; }

.mtc__status { margin: -4px 0 0; font-size: 0.78rem; font-weight: 600; color: #0b5cad; }
.mtc__error { margin: 0; font-size: 0.76rem; color: #b91c1c; }

.mtc__facts {
  list-style: none; margin: 0; padding: 0;
  display: flex; flex-direction: column; gap: 8px;
  li {
    display: flex; gap: 8px; align-items: flex-start;
    font-size: 0.78rem; line-height: 1.5; color: var(--grey-color, #5f5f5f);
    svg { flex-shrink: 0; margin-top: 3px; color: #0b5cad; }
  }
}

@media (prefers-reduced-motion: reduce) {
  .mtc__row { transition: none; }
}
</style>
